import React from 'react';
import DOMPurify from 'dompurify';
import { Card, Box, IconButton, Icon } from '@chakra-ui/react';
import {
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  Bars3Icon,
  EyeIcon,
  EyeSlashIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import { Tooltip } from '@/components/ui/tooltip';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { Collection } from '@/lib/types';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';

type Props = {
  item: Collection;
  lastContentElementRef: (node: HTMLDivElement | null) => void;
  onView: (item: Collection) => void;
  onGeneratePiblicLink: (item: Collection) => void;
  onDuplicate: (item: Collection) => void;
  onEdit: (item: Collection) => void;
  onDelete: (item: Collection) => void;
  onChangeStatus: (item: Collection, status: 'draft' | 'published') => void;
};

const ItemCard: React.FC<Props> = ({
  item,
  lastContentElementRef,
  onView,
  onGeneratePiblicLink,
  onDuplicate,
  onEdit,
  onDelete,
  onChangeStatus,
}) => {
  const isAdmin = useIsInAdminGroup();

  function stripHtmlTags(htmlString: string) {
    return DOMPurify.sanitize(htmlString, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  }

  return (
    <Card.Root key={item.reference_id} ref={lastContentElementRef} borderWidth={0}>
      <Box display='flex' alignItems={'center'}>
        <Card.Body p={2} _hover={{ cursor: 'pointer' }} onClick={() => onView(item)}>
          <Card.Title fontSize={'sm'} lineHeight={1.5}>
            {item.name}
          </Card.Title>
          <Card.Description mt={1} color={'fg.muted'} fontSize={'xs'} lineClamp='3'>
            {item.description ? stripHtmlTags(item.description) : ''}
          </Card.Description>
          <Card.Description mt={1} color={'fg.muted'} fontSize={'xs'}>
            {new Date(item.created_at).toLocaleDateString()}
          </Card.Description>
        </Card.Body>
        <Card.Footer px={2} pb={2}>
          <Box display={{ base: 'none', md: 'flex' }}>
            {isAdmin && (
              <>
                <Tooltip
                  content={`${item.status === 'published' ? 'Hide from Workspace' : 'Share to Workspace'}`}
                >
                  <IconButton
                    variant={'ghost'}
                    aria-label={`${item.status}`}
                    size={'sm'}
                    onClick={() =>
                      onChangeStatus(item, item.status === 'published' ? 'draft' : 'published')
                    }
                  >
                    <Icon as={item.status === 'published' ? EyeIcon : EyeSlashIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content={'Share public link'}>
                  <IconButton
                    variant={'ghost'}
                    aria-label={'Share public link'}
                    size={'sm'}
                    onClick={() => onGeneratePiblicLink(item)}
                  >
                    <Icon as={ShareIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content={'Duplicate'}>
                  <IconButton
                    variant={'ghost'}
                    aria-label={'Duplicate'}
                    size={'sm'}
                    onClick={() => onDuplicate(item)}
                  >
                    <Icon as={DocumentDuplicateIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content={'Edit'}>
                  <IconButton
                    variant={'ghost'}
                    aria-label={'Edit'}
                    size={'sm'}
                    onClick={() => onEdit(item)}
                  >
                    <Icon as={PencilIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content={'Delete'}>
                  <IconButton
                    variant={'ghost'}
                    aria-label={'Delete'}
                    size={'sm'}
                    onClick={() => onDelete(item)}
                  >
                    <Icon as={TrashIcon} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
          <Box display={{ base: 'block', md: 'none' }}>
            <MenuRoot>
              <MenuTrigger asChild>
                <IconButton variant='outline' aria-label='Menu' size='sm'>
                  <Icon as={Bars3Icon} />
                </IconButton>
              </MenuTrigger>
              <MenuContent>
                {isAdmin && (
                  <>
                    <MenuItem
                      value={'change-status'}
                      onClick={() =>
                        onChangeStatus(item, item.status === 'published' ? 'draft' : 'published')
                      }
                    >
                      <Icon as={item.status === 'published' ? EyeIcon : EyeSlashIcon} w={4} h={4} />
                      {item.status === 'published' ? 'Hide from Workspace' : 'Share to Workspace'}
                    </MenuItem>
                    <MenuItem value='share-public-link' onClick={() => onGeneratePiblicLink(item)}>
                      <Icon as={ShareIcon} w={4} h={4} />
                      Share public link
                    </MenuItem>
                    <MenuItem value='duplicate' onClick={() => onDuplicate(item)}>
                      <Icon as={DocumentDuplicateIcon} w={4} h={4} />
                      Duplicate
                    </MenuItem>
                    <MenuItem value='edit' onClick={() => onEdit(item)}>
                      <Icon as={PencilIcon} w={4} h={4} />
                      Edit
                    </MenuItem>
                    <MenuItem value='delete' onClick={() => onDelete(item)}>
                      <Icon as={TrashIcon} w={4} h={4} />
                      Delete
                    </MenuItem>
                  </>
                )}
              </MenuContent>
            </MenuRoot>
          </Box>
        </Card.Footer>
      </Box>
    </Card.Root>
  );
};

export default ItemCard;
